import { doc, Timestamp, updateDoc } from "firebase/firestore";
import { documentId as liteDocumentId, Timestamp as LiteTimestamp, where as liteWhere } from "firebase/firestore/lite";
import db from "../Firebase";
import { Users } from "../../models/User.tsx";
import { BetDetails } from "../../models/MyBetModel.tsx";
import { Transaction } from "../../models/User.tsx";
import { WeeklyFiguresDoc, WeeklyFiguresSummary } from "../../models/WeeklyFigure.tsx";
import { VimplayTransaction } from "../../models/VimplayTransaction.tsx";
import { ClosingLineAnalysisDoc, ClosingLineBetItem, ClosingLineLineType, ClosingLineMarketKind } from "../../models/ClosingLineAnalysis.tsx";
import {
  adminWallTimeToUtc,
  getAdminFiguresIsoWeekId as getFiguresIsoWeekId,
  getAdminFiguresWeekdayIndex as getFiguresWeekdayIndex,
} from "../../helpers/adminFiguresTime.tsx";
import { formatAmericanOdds, formatOriginalFinalBetLine, formatSpreadPoints } from "../../helpers/closingLineAnalysis.ts";
import { isFreePlayBet } from "../../helpers/functions.tsx";
import { getLiteDoc, getLiteQueryDocs, getLiteQueryDocsAt } from "../../helpers/firestoreLiteQuery.ts";

export type AnyBet = BetDetails & { id?: string };

const COMPLETED_BET_STATUSES = ['completed', 'GradedWithTransaction'];
const PENDING_BET_STATUSES = ['pending', 'GradingPending'];

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;



const chunkArray = (array: string[], size: number): string[][] => {
  const result: string[][] = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
};

export type WeeklyFiguresDayTotals = Record<string, number> & { weeklyTotal: number };

export type WeeklyFiguresDetailIds = {
  betIdsByDate: Record<string, string[]>;
  adjustmentIdsByDate: Record<string, string[]>;
  casinoIdsByDate: Record<string, string[]>;
  allBetIds: string[];
  allAdjustmentIds: string[];
  allCasinoIds: string[];
  depositWithdrawalIds: string[];
  pendingBetIds: string[];
};

export type WeeklyFiguresData = {
  players: Users[];
  subAgents: Users[];
  dailyTotals: Record<string, WeeklyFiguresDayTotals>;
  totalPendingAmountPerPlayer: Record<string, number>;
  depositsWithdrawalsByPlayer: Record<string, number>;
  detailIdsByPlayer: Record<string, WeeklyFiguresDetailIds>;
};

const emptyWeeklyFiguresData = (): WeeklyFiguresData => ({
  players: [],
  subAgents: [],
  dailyTotals: {},
  totalPendingAmountPerPlayer: {},
  depositsWithdrawalsByPlayer: {},
  detailIdsByPlayer: {},
});

const parseDateKeyToPacificNoon = (dateKey: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return adminWallTimeToUtc(year, month, day, 12, 0, 0, 0);
};

function mapWeeklyFiguresDocs(docs: WeeklyFiguresDoc[], isMaster: boolean): WeeklyFiguresData {
  const players: Users[] = [];
  const dailyTotals: Record<string, WeeklyFiguresDayTotals> = {};
  const totalPendingAmountPerPlayer: Record<string, number> = {};
  const depositsWithdrawalsByPlayer: Record<string, number> = {};
  const detailIdsByPlayer: Record<string, WeeklyFiguresDetailIds> = {};
  const parentNames = new Set<string>();

  for (const fig of docs) {
    const playerId = fig.userDocId;
    if (!playerId) continue;

    players.push({
      id: playerId,
      userName: fig.userName,
      pin: fig.pin,
      balance: fig.balance,
      parent: fig.parent,
      userType: 'Player',
    } as Users);

    if (fig.parent) parentNames.add(fig.parent);

    const dayTotals: WeeklyFiguresDayTotals = { weeklyTotal: fig.weekTotal ?? 0 };
    DAY_NAMES.forEach(day => {
      dayTotals[day] = 0;
    });

    const betIdsByDate: Record<string, string[]> = {};
    const adjustmentIdsByDate: Record<string, string[]> = {};
    const casinoIdsByDate: Record<string, string[]> = {};
    const allBetIds: string[] = [];
    const allAdjustmentIds: string[] = [];
    const allCasinoIds: string[] = [];

    for (const [dateKey, day] of Object.entries(fig.days || {})) {
      const parsed = parseDateKeyToPacificNoon(dateKey);
      if (parsed) {
        const dayName = DAY_NAMES[getFiguresWeekdayIndex(parsed)];
        dayTotals[dayName] = day.totalDay ?? 0;
      }

      const betIds = day.betIds || [];
      const adjIds = day.transactionAdjustmentIds || [];
      const casinoIds = day.casinoTransactionIds || [];
      betIdsByDate[dateKey] = betIds;
      adjustmentIdsByDate[dateKey] = adjIds;
      casinoIdsByDate[dateKey] = casinoIds;
      allBetIds.push(...betIds);
      allAdjustmentIds.push(...adjIds);
      allCasinoIds.push(...casinoIds);
    }

    dailyTotals[playerId] = dayTotals;
    totalPendingAmountPerPlayer[playerId] = fig.pending ?? 0;
    depositsWithdrawalsByPlayer[playerId] = fig.depositsWithdrawalsTotal ?? 0;
    detailIdsByPlayer[playerId] = {
      betIdsByDate,
      adjustmentIdsByDate,
      casinoIdsByDate,
      allBetIds,
      allAdjustmentIds,
      allCasinoIds,
      depositWithdrawalIds: fig.depositWithdrawalIds || [],
      pendingBetIds: fig.pendingBetIds || [],
    };
  }

  const subAgents: Users[] = isMaster
    ? [...parentNames].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
      .map(name => ({ id: name, userName: name, userType: 'subAgent' } as Users))
    : [];

  return {
    players,
    subAgents,
    dailyTotals,
    totalPendingAmountPerPlayer,
    depositsWithdrawalsByPlayer,
    detailIdsByPlayer,
  };
}

export async function getBetsByIds(ids: string[]): Promise<AnyBet[]> {
  const unique = [...new Set(ids.filter(Boolean))];
  if (unique.length === 0) return [];

  try {
    const chunks = chunkArray(unique, 30);
    const docs = await Promise.all(
      chunks.map(chunk =>
        getLiteQueryDocs('myBet', [liteWhere(liteDocumentId(), 'in', chunk)])
      )
    );
    return docs.flat().map(d => ({ id: d.id, ...(d.data() as AnyBet) }));
  } catch (error) {
    console.error('Error getBetsByIds:', error);
    return [];
  }
}

export async function getPendingBetsForPlayer(userDocId: string): Promise<AnyBet[]> {
  if (!userDocId) return [];

  try {
    const docs = await getLiteQueryDocs('myBet', [
      liteWhere('player.userDocId', '==', userDocId),
      liteWhere('betStatus', 'in', PENDING_BET_STATUSES),
    ]);
    return docs
      .map(d => ({ id: d.id, ...(d.data() as AnyBet) }))
      .filter(bet => !isFreePlayBet(bet));
  } catch (error) {
    console.error('Error getPendingBetsForPlayer:', error);
    return [];
  }
}

export async function getTransactionsByIds(ids: string[]): Promise<Transaction[]> {
  const unique = [...new Set(ids.filter(Boolean))];
  if (unique.length === 0) return [];

  try {
    const chunks = chunkArray(unique, 30);
    const docs = await Promise.all(
      chunks.map(chunk =>
        getLiteQueryDocs('transaction', [liteWhere(liteDocumentId(), 'in', chunk)])
      )
    );
    return docs.flat().map(d => ({ id: d.id, ...(d.data() as Transaction) }));
  } catch (error) {
    console.error('Error getTransactionsByIds:', error);
    return [];
  }
}

const resolvePlayerDocId = async (userName: string): Promise<string> => {
  const name = userName.trim();
  if (!name) return "";
  const docs = await getLiteQueryDocs("users", [liteWhere("userName", "==", name)]);
  return docs[0]?.id ?? "";
};

/**
 * Casino ledger rows for one player. playerId is the user document id.
 * Pass userName when only the login name is known.
 */
export async function getVimplayTransactionsForPlayer(
  lookup: { userName?: string; playerId?: string },
  since?: Date,
  until?: Date,
): Promise<VimplayTransaction[]> {
  try {
    const playerId = lookup.playerId?.trim()
      || await resolvePlayerDocId(lookup.userName ?? "");
    if (!playerId) return [];

    const constraints = [liteWhere("playerId", "==", playerId)];
    if (since) {
      constraints.push(liteWhere("createdAt", ">=", LiteTimestamp.fromDate(since)));
    }
    if (until) {
      constraints.push(liteWhere("createdAt", "<=", LiteTimestamp.fromDate(until)));
    }

    const docs = await getLiteQueryDocs("vimplayTransactions", constraints);
    return docs
      .map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() as VimplayTransaction),
      }))
      .filter((tx) => tx.kind !== "refund" && (tx.status == null || Number(tx.status) === 111));
  } catch (error) {
    if ((since || until) && isMissingIndexError(error)) {
      return getVimplayTransactionsForPlayer(
        { playerId: lookup.playerId, userName: lookup.userName },
      );
    }
    console.error("Error getVimplayTransactionsForPlayer:", error);
    return [];
  }
}

export async function getVimplayTransactionsByIds(ids: string[]): Promise<VimplayTransaction[]> {
  const unique = [...new Set(ids.filter(Boolean))];
  if (unique.length === 0) return [];

  try {
    const chunks = chunkArray(unique, 30);
    const docs = await Promise.all(
      chunks.map(chunk =>
        getLiteQueryDocs('vimplayTransactions', [liteWhere(liteDocumentId(), 'in', chunk)])
      )
    );
    return docs.flat().map(d => ({ id: d.id, ...(d.data() as VimplayTransaction) }));
  } catch (error) {
    console.error('Error getVimplayTransactionsByIds:', error);
    return [];
  }
}

export async function showPlayersBets(user: Users): Promise<{ bets: AnyBet[] }> {
  const scopeField =
    user.userType === "masterAgent" ? "player.master"
      : user.userType === "subAgent" ? "player.parent"
        : null;

  if (!scopeField) {
    return { bets: [] };
  }

  const docs = await getLiteQueryDocs("myBet", [
    liteWhere(scopeField, "==", user.userName),
    liteWhere("betStatus", "in", PENDING_BET_STATUSES),
  ]);

  return {
    bets: docs.map(docSnap => ({ id: docSnap.id, ...(docSnap.data() as AnyBet) })),
  };
}

export async function getAgentSummary(currentUser: Pick<Users, "userName">): Promise<WeeklyFiguresSummary | null> {
  try {
    const snapshot = await getLiteDoc("configs", currentUser.userName);
    if (!snapshot.exists()) return null;
    return snapshot.data() as WeeklyFiguresSummary;
  } catch (error) {
    const code = (error as { code?: string })?.code;
    // Expected when auth is cleared mid-flight (session expire / logout).
    if (code !== "permission-denied") {
      console.error("Error getAgentSummary:", error);
    }
    throw error;
  }
}


export async function pendingBets(
  currentUser: Users,
  startDate: Date,
): Promise<WeeklyFiguresData> {
  const weekId = getFiguresIsoWeekId(startDate);

  const field =
    currentUser.userType === "masterAgent"
      ? "master"
      : currentUser.userType === "subAgent"
      ? "parent"
      : null;

  if (!field) {
    return emptyWeeklyFiguresData();
  }

  try {
    const docs = await getLiteQueryDocs("weeklyFigures", [
      liteWhere("weekId", "==", weekId),
      liteWhere(field, "==", currentUser.userName),
    ]);

    return mapWeeklyFiguresDocs(
      docs.map(docSnap => docSnap.data() as WeeklyFiguresDoc),
      currentUser.userType === "masterAgent"
    );
  } catch (error) {
    console.error("Error:", error);
    return emptyWeeklyFiguresData();
  }
}

export async function editBetByField(fieldValue: any, newData: any) {
  try {
    const docs = await getLiteQueryDocs('myBet', [
      liteWhere('ticket', '==', fieldValue),
    ]);
    const betDoc = docs[0];
    const betRef = doc(db, 'myBet', betDoc.id);

    newData.dateDelete = Timestamp.fromDate(new Date());
    newData.betStatus = 'Deleted';


    await updateDoc(betRef, newData);
  } catch (error) {
    throw error;
  }
}

export async function searchBetsUsers(
  userName: string,
  range: number,
  userType: string,
  specificUser?: string,
  options?: { agentScope?: string[] }
): Promise<AnyBet[]> {
  try {
    const today = LiteTimestamp.now();
    const rangeInSeconds = range * 24 * 60 * 60;
    const start = new LiteTimestamp(today.seconds - rangeInSeconds, today.nanoseconds);
    const end = today;

    const mapBetDocs = (docs: Awaited<ReturnType<typeof getLiteQueryDocs>>) =>
      docs.map(docSnap => ({ id: docSnap.id, ...(docSnap.data() as AnyBet) }));

    const fetchDeletedBetsByUserName = async (playerUserName: string): Promise<AnyBet[]> => {
      const snapshot = await getLiteQueryDocs("myBet", [
        liteWhere("player.userName", "==", playerUserName),
        liteWhere("betStatus", "==", "Deleted"),
        liteWhere("dateDelete", ">=", start),
        liteWhere("dateDelete", "<=", end),
      ]);
      return mapBetDocs(snapshot);
    };

    const fetchDeletedBetsByParentScope = async (parentScope: string[]): Promise<AnyBet[]> => {
      if (parentScope.length === 0) return [];

      const constraintsForChunk = (chunk: string[]) => [
        chunk.length === 1
          ? liteWhere("player.parent", "==", chunk[0])
          : liteWhere("player.parent", "in", chunk),
        liteWhere("betStatus", "==", "Deleted"),
        liteWhere("dateDelete", ">=", start),
        liteWhere("dateDelete", "<=", end),
      ];

      if (parentScope.length === 1) {
        const snapshot = await getLiteQueryDocs("myBet", constraintsForChunk(parentScope));
        return mapBetDocs(snapshot);
      }

      const chunks = chunkArray(parentScope, 10);
      const snapshots = await Promise.all(chunks.map(chunk => getLiteQueryDocs("myBet", constraintsForChunk(chunk))));
      return snapshots.flatMap(mapBetDocs);
    };

    if (specificUser) {
      return fetchDeletedBetsByUserName(specificUser);
    }

    if (userType === "subAgent") {
      return fetchDeletedBetsByParentScope([userName]);
    }

    if (userType === "masterAgent") {
      let agentScope = options?.agentScope;

      if (!agentScope) {
        const agentsSnapshot = await getLiteQueryDocs("users", [
          liteWhere("parent", "==", userName),
          liteWhere("userType", "==", "subAgent"),
        ]);
        agentScope = agentsSnapshot.map(docSnap => docSnap.data().userName);
      }

      return fetchDeletedBetsByParentScope(agentScope);
    }

    return [];

  } catch (error) {
    console.error("Error in searchBetsUsers:", error);
    throw error;
  }
}


export async function getUserIdByUsername(userName: string): Promise<string | null> {
  try {
    const docs = await getLiteQueryDocs('users', [
      liteWhere('userName', '==', userName),
    ]);

    if (docs.length > 0) {
      return docs[0].id;
    } else {
      return null;
    }
  } catch (error) {
    throw error;
  }
}

export async function showUserbets(userName: string): Promise<AnyBet[]> {
  try {
    const docs = await getLiteQueryDocs("myBet", [
      liteWhere("player.userName", "==", userName),
      liteWhere("betStatus", "in", PENDING_BET_STATUSES),
    ]);

    return docs.map(docSnap => ({
      id: docSnap.id,
      ...(docSnap.data() as AnyBet),
    }));
  } catch (error) {
    console.error('Error showUserbets:', error);
    throw error;
  }
}

export async function showUserbetsUser(userName: string): Promise<AnyBet[]> {
  try {

    const docs = await getLiteQueryDocs("myBet", [
      liteWhere("player.userName", "==", userName),
      liteWhere("betStatus", "in", [
        "pending",
        "GradingPending"
      ]),
      liteWhere("betType", "!=", "Free Play"),
    ]);

    const bets = docs
      .map(docSnap => {
        const data = docSnap.data() as AnyBet;
        return {
          id: docSnap.id,
          ...data,
        };
      });

    return bets;
  } catch (error) {
    throw error;
  }
}

export async function showBetsFreePlays(userName: string): Promise<BetDetails[]> {
  const constraintSets = [
    [liteWhere("player.userName", "==", userName), liteWhere("betType", "==", "Free Play")],
    [liteWhere("player.userName", "==", userName), liteWhere("freeBet", "==", "FP")],
    [liteWhere("player.userName", "==", userName), liteWhere("isFreePlay", "==", true)],
  ];

  const snapshots = await Promise.all(
    constraintSets.map(async constraints => {
      try {
        return await getLiteQueryDocs("myBet", constraints);
      } catch (error) {
        console.error("Error showBetsFreePlays query:", error);
        return [];
      }
    }),
  );

  const byId = new Map<string, BetDetails>();
  snapshots.flat().forEach(docSnap => {
    const bet = { id: docSnap.id, ...(docSnap.data() as BetDetails) };
    if (isFreePlayBet(bet) || bet.isFreePlay === true) {
      byId.set(docSnap.id, bet);
    }
  });

  return [...byId.values()];
}


// export const getCompletedBetsByPlayer = async (username: string) => {
//   try {
//     const betsSnapshot = await getDocs(
//       query(collection(db, 'myBet'), where('player.userName', '==', username), where('betStatus', '==', 'Completed'))
//     );

//     const completedBets = betsSnapshot.docs.map(doc => ({
//       id: doc.id,
//       ...(doc.data() as AnyBet),
//     }));
//     return completedBets;
//   } catch (error) {
//     console.error('Error getting completed bets:', error);
//     return [];
//   }
// };

export type PlayerHistoryPage<T> = {
  items: T[];
  isFullHistory: boolean;
};

const isMissingIndexError = (error: unknown) =>
  (error as { code?: string })?.code === 'failed-precondition';

const queryCompletedBetsByPlayer = async (username: string, since?: Date) => {
  const constraints = [
    liteWhere('player.userName', '==', username),
    liteWhere('betStatus', 'in', COMPLETED_BET_STATUSES),
  ];
  if (since) {
    constraints.push(liteWhere('lastUpdateBySystem', '>=', LiteTimestamp.fromDate(since)));
  }

  const docs = await getLiteQueryDocs('myBet', constraints);
  return docs.map(docSnap => ({
    id: docSnap.id,
    ...(docSnap.data() as AnyBet),
  }));
};

const queryAdjustmentTransactionsByPlayer = async (username: string, since?: Date) => {
  const constraints = [
    liteWhere('customer', '==', username),
    liteWhere('transactionType', 'in', ['CreditAdjustment', 'DebitAdjustment']),
  ];
  if (since) {
    constraints.push(liteWhere('createDate', '>=', LiteTimestamp.fromDate(since)));
  }

  const docs = await getLiteQueryDocs('transaction', constraints);
  return docs.map(docSnap => ({
    id: docSnap.id,
    ...(docSnap.data() as Transaction),
  }));
};

export const getCompletedBetsByPlayer = async (
  username: string,
  since?: Date
): Promise<PlayerHistoryPage<AnyBet>> => {
  try {
    return {
      items: await queryCompletedBetsByPlayer(username, since),
      isFullHistory: !since,
    };
  } catch (error) {
    if (since && isMissingIndexError(error)) {
      try {
        return {
          items: await queryCompletedBetsByPlayer(username),
          isFullHistory: true,
        };
      } catch (inner) {
        console.error('Error getting completed bets:', inner);
        return { items: [], isFullHistory: true };
      }
    }
    console.error('Error getting completed bets:', error);
    return { items: [], isFullHistory: !since };
  }
};

export const getTransactionsByPlayer = async (
  username: string,
  since?: Date
): Promise<PlayerHistoryPage<Transaction>> => {
  try {
    return {
      items: await queryAdjustmentTransactionsByPlayer(username, since),
      isFullHistory: !since,
    };
  } catch (error) {
    if (since && isMissingIndexError(error)) {
      try {
        return {
          items: await queryAdjustmentTransactionsByPlayer(username),
          isFullHistory: true,
        };
      } catch (inner) {
        console.error('Error getting transactions:', inner);
        return { items: [], isFullHistory: true };
      }
    }
    console.error('Error getting transactions:', error);
    return { items: [], isFullHistory: !since };
  }
};

export const CLOSING_LINE_CACHE_TTL_MS = 60_000;
export const CLOSING_LINE_CACHE_STALE_MS = 5 * 60_000;
export const closingLineAgentCacheKey = (userName: string) => `closingLineAnalysis:${userName}`;

const scopeFieldForUser = (userType?: string): "master" | "parent" | null => {
  if (userType === "masterAgent") return "master";
  if (userType === "subAgent") return "parent";
  return null;
};

const mapClosingLineDoc = (docSnap: { id: string; data: () => unknown }): ClosingLineAnalysisDoc => {
  const data = (docSnap.data() as ClosingLineAnalysisDoc) ?? ({} as ClosingLineAnalysisDoc);
  return {
    ...data,
    userDocId: data.userDocId || docSnap.id,
  };
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const pickString = (raw: Record<string, unknown>, keys: string[]): string => {
  for (const key of keys) {
    const value = raw[key];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return "";
};

const pickNumber = (raw: Record<string, unknown>, keys: string[]): number | null => {
  for (const key of keys) {
    const value = raw[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
};

const pickTimestamp = (raw: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = raw[key];
    if (value) return value as ClosingLineBetItem["acceptedAt"];
  }
  return undefined;
};

const normalizeKind = (raw: Record<string, unknown>): ClosingLineMarketKind => {
  const marketKind = pickString(raw, ["marketKind"]).toLowerCase();
  if (marketKind === "moneyline" || marketKind === "spread" || marketKind === "total") {
    return marketKind;
  }
  const text = pickString(raw, ["kind", "lineType", "market", "marketName"]).toLowerCase();
  if (text.includes("money") || text === "ml" || text === "price") return "moneyline";
  if (text.includes("total") || text.includes("over") || text.includes("under")) return "total";
  if (text.includes("spread") || text.includes("run") || text.includes("puck") || text === "points") return "spread";
  const clvPrice = pickNumber(raw, ["price"]);
  const clvPoints = pickNumber(raw, ["points"]);
  if ((clvPrice ?? 0) !== 0 && (clvPoints ?? 0) === 0) return "moneyline";
  return "spread";
};

const mapClosingLineBet = (docSnap: { id: string; data: () => unknown }): ClosingLineBetItem => {
  const raw = asRecord(docSnap.data());
  const kind = normalizeKind(raw);
  const tookPrice = pickNumber(raw, ["tookPrice"]);
  const tookPoints = pickNumber(raw, ["tookPoints"]);
  const closedPrice = pickNumber(raw, ["closedPrice"]);
  const closedPoints = pickNumber(raw, ["closedPoints"]);
  const clvPrice = pickNumber(raw, ["price"]);
  const clvPoints = pickNumber(raw, ["points"]);
  const taken = kind === "moneyline"
    ? formatAmericanOdds(tookPrice)
    : formatSpreadPoints(tookPoints);
  const closing = kind === "moneyline"
    ? formatAmericanOdds(closedPrice)
    : formatSpreadPoints(closedPoints);
  const value = kind === "moneyline" ? clvPrice : clvPoints;

  return {
    betId: pickString(raw, ["betId"]) || docSnap.id,
    ticket: pickString(raw, ["ticket", "ticketId"]),
    acceptedAt: pickTimestamp(raw, ["lastUpdateBySystem", "betDate", "acceptedAt", "gradedAt"]),
    betDate: pickTimestamp(raw, ["betDate", "lastUpdateBySystem"]),
    description: pickString(raw, ["description", "betName", "selection", "name"]),
    kind,
    lineType: kind === "moneyline" ? "price" : "points",
    taken: taken === "—" ? "" : taken,
    closing: closing === "—" ? "" : closing,
    originalBet: pickString(raw, ["originalBet"])
      || formatOriginalFinalBetLine(kind, tookPoints, tookPrice),
    finalBet: pickString(raw, ["finalBet"])
      || formatOriginalFinalBetLine(kind, closedPoints, closedPrice),
    value,
    valueDisplay: "",
    winLoss: pickNumber(raw, ["winLoss", "result", "net"]) ?? 0,
  };
};

const buildClosingLineDescription = (bet: AnyBet, taken: string, ticket?: string): string => {
  const sport = String(bet.sport ?? "").trim();
  const ticketLabel = String(ticket || bet.ticket || "").trim();
  const name = String(bet.betName || bet.odd?.name || bet.odd?.selection || "").trim();
  const market = String(bet.marketName || bet.odd?.market || "").trim();
  const parts = [
    sport,
    ticketLabel ? `#${ticketLabel}` : "",
    name,
    taken && !name.includes(taken) ? taken : "",
  ].filter(Boolean);
  if (market && !parts.join(" ").toLowerCase().includes(market.toLowerCase())) {
    parts.push(`- ${market}`);
  }
  return parts.join(" ");
};

const enrichClosingLineBets = async (bets: ClosingLineBetItem[]): Promise<ClosingLineBetItem[]> => {
  const ids = bets.map((bet) => bet.betId || bet.ticket).filter(Boolean);
  const sources = await getBetsByIds(ids);
  if (!sources.length) return bets;

  const byId = new Map(sources.map((bet) => [bet.id ?? bet.ticket ?? "", bet]));
  return bets.map((bet) => {
    const source = byId.get(bet.betId || bet.ticket);
    if (!source) return bet;
    const taken = bet.taken || (
      bet.kind === "moneyline"
        ? formatAmericanOdds(source.odd?.price)
        : formatSpreadPoints(source.odd?.points)
    );
    return {
      ...bet,
      ticket: bet.ticket || String(source.ticket ?? ""),
      acceptedAt: source.betDate ?? bet.acceptedAt,
      taken: taken === "—" ? bet.taken : taken,
      description: buildClosingLineDescription(
        source,
        taken === "—" ? "" : taken,
        bet.ticket || String(source.ticket ?? ""),
      ),
    };
  });
};

const loadClosingLineBets = async (userDocId: string): Promise<ClosingLineBetItem[]> => {
  if (!userDocId) return [];
  const docs = await getLiteQueryDocsAt(["closingLineAnalysis", userDocId, "bets"]);
  const bets = docs
    .map(mapClosingLineBet)
    .sort((a, b) => {
      const aTime = a.acceptedAt && typeof (a.acceptedAt as { seconds?: number }).seconds === "number"
        ? (a.acceptedAt as { seconds: number }).seconds
        : 0;
      const bTime = b.acceptedAt && typeof (b.acceptedAt as { seconds?: number }).seconds === "number"
        ? (b.acceptedAt as { seconds: number }).seconds
        : 0;
      return bTime - aTime;
    });
  return enrichClosingLineBets(bets);
};

export async function getClosingLineAnalysis(currentUser: Users): Promise<ClosingLineAnalysisDoc[]> {
  const field = scopeFieldForUser(currentUser.userType);
  if (!field || !currentUser.userName) return [];

  try {
    const docs = await getLiteQueryDocs("closingLineAnalysis", [
      liteWhere(field, "==", currentUser.userName),
    ]);

    return docs
      .map(mapClosingLineDoc)
      .sort((a, b) => a.userName.localeCompare(b.userName, undefined, { numeric: true, sensitivity: "base" }));
  } catch (error) {
    console.error("Error getClosingLineAnalysis:", error);
    return [];
  }
}

export async function getPlayerClosingLineAnalysis(userName: string): Promise<ClosingLineAnalysisDoc | null> {
  if (!userName) return null;

  try {
    const docs = await getLiteQueryDocs("closingLineAnalysis", [
      liteWhere("userName", "==", userName),
    ]);
    if (!docs.length) return null;
    const player = mapClosingLineDoc(docs[0]);
    const bets = await loadClosingLineBets(player.userDocId);
    return { ...player, bets };
  } catch (error) {
    console.error("Error getPlayerClosingLineAnalysis:", error);
    return null;
  }
}